import { useState } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import {
  FlaskConical,
  Menu,
  UsersRound,
  User,
  BarChart3,
  Settings,
  LogOut,
  Shield,
  Globe,
} from 'lucide-react'
import { isAdmin, isSuperAdmin } from '@/types/auth'
import ThemeToggle from './ThemeToggle'
import AnimatedLogo from './AnimatedLogo'
import { useAuth } from '@/hooks'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'

const Header = () => {
  const [isOpen, setIsOpen] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout } = useAuth()

  const navLinks = [
    { href: '/', label: 'Início', isHome: true },
    { href: '/cursos', label: 'Cursos', isRoute: true },
    { href: '/#drops', label: 'Drops', scrollTo: 'drops' },
    { href: '/#masterclasses', label: 'Masterclasses', scrollTo: 'masterclasses' },
  ]

  const closeMenu = () => setIsOpen(false)

  const handleLogout = () => {
    logout()
  }

  // Função especial para o botão Início
  const handleHomeClick = (e: React.MouseEvent) => {
    e.preventDefault()

    // Se estiver na home, faz scroll para o topo
    if (location.pathname === '/' || location.pathname === '/home') {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } else {
      // Se estiver em outra página, navega para home
      navigate('/home')
    }
    closeMenu()
  }

  // Função para navegar ou fazer scroll suave
  const handleNavClick = (e: React.MouseEvent, link: (typeof navLinks)[number]) => {
    // Se tem scrollTo e estamos na home
    if (link.scrollTo && (location.pathname === '/' || location.pathname === '/home')) {
      e.preventDefault()
      const element = document.getElementById(link.scrollTo)
      if (element) {
        const headerOffset = 80
        const elementPosition = element.getBoundingClientRect().top + window.scrollY
        window.scrollTo({ top: elementPosition - headerOffset, behavior: 'smooth' })
        window.dispatchEvent(new CustomEvent('nav-section-click', { detail: link.scrollTo }))
        closeMenu()
      }
    }
    // Se tem scrollTo mas NÃO estamos na home, navega para home com hash
    else if (link.scrollTo) {
      e.preventDefault()
      navigate(`/home#${link.scrollTo}`)
      closeMenu()
    }
  }

  return (
    <header
      className="sticky z-50 w-full border-b border-border bg-background/80 backdrop-blur-lg"
      style={{ top: 'var(--maintenance-banner-height, 0px)' }}
    >
      <div className="container flex h-16 items-center justify-between">
        <a href="/" onClick={handleHomeClick} className="flex items-center gap-2 cursor-pointer">
          <AnimatedLogo size="md" />
        </a>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((link) =>
            link.isHome ? (
              <a
                key={link.href}
                href={link.href}
                onClick={handleHomeClick}
                className="nav-link text-sm font-medium cursor-pointer"
              >
                {link.label}
              </a>
            ) : link.scrollTo ? (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNavClick(e, link)}
                className="nav-link text-sm font-medium cursor-pointer"
              >
                {link.label}
              </a>
            ) : (
              <a key={link.href} href={link.href} className="nav-link text-sm font-medium">
                {link.label}
              </a>
            )
          )}
          {/* Temporariamente desabilitado
          <a href="https://sym-fc.lovable.app/" target="_blank" rel="noopener noreferrer" className="text-sm font-bold text-orange-500 hover:text-orange-400 transition-colors flex items-center gap-1">
            <FlaskConical className="w-4 h-4" />
            Sym! Labs
          </a>
          <Link to="/hub" className="text-sm font-bold text-orange-500 hover:text-orange-400 transition-colors flex items-center gap-1">
            <UsersRound className="w-4 h-4" />
            Sym! Hub
          </Link>
          */}
        </nav>

        <div className="flex items-center gap-2">
          <ThemeToggle />

          {/* Botão visível apenas para usuários Microsoft */}
          {user?.authProvider === 'microsoft' && (
            <Link to="/acesse-symphony" className="btn-primary text-sm hidden sm:block">
              Acesse AI Symphony
            </Link>
          )}

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center justify-center w-10 h-10 rounded-full hover:opacity-80 transition-opacity focus:outline-none focus:ring-2 focus:ring-primary/50"
                  title="Minha conta"
                >
                  <Avatar className="w-10 h-10 border-2 border-border">
                    <AvatarImage src={user.avatar || ''} alt={user.name} />
                    <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                      {user.name.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="w-48 bg-popover border border-border shadow-lg"
              >
                <DropdownMenuItem
                  onClick={() => navigate('/perfil?tab=progress')}
                  className="cursor-pointer"
                >
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Meu Progresso
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate('/perfil?tab=settings')}
                  className="cursor-pointer"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Configurações
                </DropdownMenuItem>
                {isAdmin(user?.role) && !isSuperAdmin(user?.role) && (
                  <DropdownMenuItem onClick={() => navigate('/admin')} className="cursor-pointer">
                    <Shield className="w-4 h-4 mr-2" />
                    Admin
                  </DropdownMenuItem>
                )}
                {isSuperAdmin(user?.role) && (
                  <DropdownMenuItem
                    onClick={() => navigate('/super-admin')}
                    className="cursor-pointer"
                  >
                    <Globe className="w-4 h-4 mr-2" />
                    Super Admin
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="cursor-pointer text-destructive focus:text-destructive"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Sair
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button
              onClick={() => navigate('/auth')}
              variant="ghost"
              className="text-sm font-medium"
            >
              Entrar
            </Button>
          )}

          {/* Mobile Menu */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden">
                <Menu className="h-5 w-5" />
                <span className="sr-only">Abrir menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[280px] sm:w-[320px]">
              <SheetHeader>
                <SheetTitle>
                  <AnimatedLogo size="sm" />
                </SheetTitle>
              </SheetHeader>
              <nav className="flex flex-col gap-4 mt-8">
                {navLinks.map((link) =>
                  link.isHome ? (
                    <a
                      key={link.href}
                      href={link.href}
                      onClick={handleHomeClick}
                      className="text-foreground hover:text-primary text-lg font-medium transition-colors py-2 cursor-pointer"
                    >
                      {link.label}
                    </a>
                  ) : link.scrollTo ? (
                    <a
                      key={link.href}
                      href={link.href}
                      onClick={(e) => handleNavClick(e, link)}
                      className="text-foreground hover:text-primary text-lg font-medium transition-colors py-2 cursor-pointer"
                    >
                      {link.label}
                    </a>
                  ) : (
                    <a
                      key={link.href}
                      href={link.href}
                      onClick={closeMenu}
                      className="text-foreground hover:text-primary text-lg font-medium transition-colors py-2"
                    >
                      {link.label}
                    </a>
                  )
                )}
                {/* Temporariamente desabilitado
                <a
                  href="https://sym-fc.lovable.app/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-lg font-bold text-orange-500 hover:text-orange-400 transition-colors flex items-center gap-2 py-2"
                >
                  <FlaskConical className="w-5 h-5" />
                  Sym! Labs
                </a>
                <Link
                  to="/hub"
                  onClick={closeMenu}
                  className="text-lg font-bold text-orange-500 hover:text-orange-400 transition-colors flex items-center gap-2 py-2"
                >
                  <UsersRound className="w-5 h-5" />
                  Sym! Hub
                </Link>
                */}
                <div className="pt-4 border-t border-border mt-4 space-y-3">
                  {user ? (
                    <>
                      <Link
                        to="/perfil"
                        onClick={closeMenu}
                        className="flex items-center gap-2 text-foreground hover:text-primary text-lg font-medium transition-colors py-2"
                      >
                        <User className="w-5 h-5" />
                        Minha Conta
                      </Link>
                      <Button
                        onClick={() => {
                          handleLogout()
                          closeMenu()
                        }}
                        variant="ghost"
                        className="w-full text-destructive hover:text-destructive hover:bg-destructive/10 justify-start"
                      >
                        <LogOut className="w-5 h-5 mr-2" />
                        Sair
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        onClick={() => {
                          navigate('/auth')
                          closeMenu()
                        }}
                        variant="outline"
                        className="w-full"
                      >
                        Entrar
                      </Button>
                    </>
                  )}
                </div>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  )
}

export default Header
